import React from 'react'
import { Box, Avatar, Tooltip, Typography, Stack, Chip } from '@mui/material'
import { Verified, LocationOn } from '@mui/icons-material'
import { getPhysicianProfileImage } from 'src/pages/Physicians/helpers'

/**
 * ProfileHeader - Compact centered profile header
 * Features:
 * - Profile photo
 * - Name with verification badge
 * - Headline/title
 * - Location
 */
const ProfileHeader = ({ profile, profileType = 'candidate' }) => {
  // Use the same helper function as FileSection to get the profile image URL
  const profileImageUrl = getPhysicianProfileImage(profile.profileImage)

  // Get headline based on profile type
  const getHeadline = () => {
    if (profileType === 'candidate') {
      const specialty = profile.specialties?.[0]?.name || 'Healthcare Professional'
      const experience = profile.yearsExperience
        ? `${profile.yearsExperience} Years Experience`
        : 'Healthcare Professional'
      return `${specialty} | ${experience}`
    } else if (profileType === 'employer') {
      return profile.jobTitle || 'Recruiter'
    }
    return ''
  }

  // Determine search status
  const getSearchStatus = () => {
    if (profileType !== 'candidate') return null
    if (profile.searchStatus === 'actively_searching') {
      return { label: 'Actively Searching', color: 'success' }
    } else if (profile.searchStatus === 'open_to_opportunities') {
      return { label: 'Open to Opportunities', color: 'info' }
    }
    return null
  }

  const searchStatus = getSearchStatus()

  return (
    <Box
      sx={{
        width: '100%',
        py: 2,
        textAlign: 'center',
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Profile Photo */}
      <Avatar
        src={profileImageUrl}
        sx={{
          width: 80,
          height: 80,
          mx: 'auto',
          mb: 1.5,
          border: '3px solid',
          borderColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      />

      {/* Name and Verification */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
      >
        <Typography variant="h5" fontWeight={700}>
          {profile.firstName} {profile.lastName}
        </Typography>
        {profile.emailVerifiedAt && (
          <Tooltip title="Email Verified" arrow>
            <Verified sx={{ color: 'primary.main', fontSize: 22 }} />
          </Tooltip>
        )}
      </Stack>

      {/* Headline */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 400 }}>
        {getHeadline()}
      </Typography>

      {/* Location and Status */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mt: 1.5 }}
      >
        {profile.city && profile.state && (
          <Chip
            icon={<LocationOn />}
            label={`${profile.city}, ${profile.state}`}
            variant="outlined"
            size="small"
          />
        )}
        {searchStatus && (
          <Chip
            label={searchStatus.label}
            color={searchStatus.color}
            size="small"
            sx={{
              fontWeight: 600,
              '&::before': {
                content: '""',
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: `${searchStatus.color}.main`,
                display: 'inline-block',
                mr: 0.5,
                animation: 'pulse 2s infinite',
              },
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
        )}
      </Stack>
    </Box>
  )
}

export default ProfileHeader
